import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiFilter, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { TbArrowsSort } from 'react-icons/tb';
import { useNdTheme } from '../utils/ndTheme';
import '../styles/newDesign.css';

const LEAVE_TYPES = {
  leave: { label: 'Отгул', cls: 'nd-t-leave', color: 'var(--nd-blue)' },
  vacation: { label: 'Отпуск', cls: 'nd-t-vac', color: 'var(--nd-green)' },
  sick: { label: 'Больничный', cls: 'nd-t-sick', color: 'var(--nd-red)' },
  remote: { label: 'На удаленке', cls: 'nd-t-remote', color: 'var(--nd-violet)' },
};

const APPROVER_ROLES = ['hr', 'admin', 'руководитель'];

function formatDate(value) {
  if (!value) return '—';
  const raw = String(value).slice(0, 10);
  const parts = raw.split('-');
  if (parts.length !== 3) return raw;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function formatDuration(leave) {
  const minutes = Number(leave.minutes) || 0;
  if (leave.type !== 'leave' || minutes <= 0) return 'Полный день';
  if (minutes < 60) return `${minutes} мин`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function readStoredUser() {
  const raw = localStorage.getItem('user') || localStorage.getItem('currentUser');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export default function Leaves({ token, onClose, isMobile = false }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ startDate: '', endDate: '', reason: '', type: 'leave', hours: 0, minutesOnly: 0 });
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('desc');
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removingIds, setRemovingIds] = useState([]);
  const [toasts, setToasts] = useState([]);
  const toastSeq = useRef(0);

  const pushToast = useCallback((text, tone) => {
    const id = ++toastSeq.current;
    setToasts(prev => [...prev, { id, text, tone }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2800);
  }, []);

  const fetchLeaves = useCallback(async () => {
    try {
      const response = await fetch('/api/leaves', { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setLeaves(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('[LEAVES] Ошибка загрузки данных:', error);
      if (error.message !== 'HTTP 401') setLeaves([]);
    }
  }, [token]);

  useEffect(() => {
    fetchLeaves();
    setUser(readStoredUser());
    const interval = setInterval(fetchLeaves, 5000);
    return () => clearInterval(interval);
  }, [fetchLeaves]);

  const userName = user?.username || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Сотрудник';
  const canApprove = APPROVER_ROLES.includes(String(user?.role || '').toLowerCase());

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (saving) return;

    const stored = readStoredUser();
    const userId = stored?.id || stored?.userId;
    if (!userId) {
      pushToast('⚠ Не найден пользователь — войдите заново', 'nd-red');
      return;
    }
    if (!form.startDate) {
      pushToast('⚠ Укажите дату начала', 'nd-amber');
      return;
    }
    const endDate = form.endDate && form.endDate >= form.startDate ? form.endDate : form.startDate;

    const totalMinutes = form.type === 'leave'
      ? (Number(form.hours || 0) * 60 + Number(form.minutesOnly || 0))
      : 0;

    setSaving(true);
    try {
      const response = await fetch('/api/leaves', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          startDate: form.startDate,
          endDate,
          reason: form.reason,
          type: form.type,
          minutes: Number(totalMinutes) || 0,
          userId
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);

      setForm({ startDate: '', endDate: '', reason: '', type: 'leave', hours: 0, minutesOnly: 0 });
      fetchLeaves();
      pushToast('📨 Заявка создана и ждёт одобрения', 'nd-amber');
    } catch (error) {
      console.error('Ошибка создания заявки:', error);
      pushToast(`⚠ ${error.message}`, 'nd-red');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту заявку?')) return;

    // Карточка уезжает вправо, и только потом уходит запрос — как в макете
    setRemovingIds(prev => [...prev, id]);
    await new Promise(resolve => setTimeout(resolve, 220));

    try {
      const response = await fetch(`/api/leaves/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      fetchLeaves();
      pushToast('🗑 Заявка удалена', 'nd-red');
    } catch (error) {
      console.error('Ошибка удаления заявки:', error);
      pushToast(`⚠ ${error.message}`, 'nd-red');
    } finally {
      setRemovingIds(prev => prev.filter(x => x !== id));
    }
  };

  const handleStatus = async (id, status) => {
    try {
      const response = await fetch(`/api/leaves/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      fetchLeaves();
      window.dispatchEvent(new CustomEvent('leaves-worktime-updated'));
      pushToast(status === 'approved' ? '✅ Заявка одобрена' : '⛔ Заявка отклонена', status === 'approved' ? undefined : 'nd-red');
    } catch (error) {
      console.error('Ошибка смены статуса:', error);
      pushToast(`⚠ ${error.message}`, 'nd-red');
    }
  };

  let filtered = Array.isArray(leaves) ? leaves : [];

  if (filter.startsWith('type:')) {
    const wanted = filter.slice(5);
    filtered = filtered.filter(l => (l.type || 'leave') === wanted);
  } else if (filter === 'future' || filter === 'past') {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    filtered = filtered.filter(l => {
      if (filter === 'future') {
        const startDate = new Date(l.startDate);
        startDate.setHours(0, 0, 0, 0);
        return startDate >= now;
      }
      const endDate = new Date(l.endDate);
      endDate.setHours(23, 59, 59, 999);
      return endDate < now;
    });
  }

  filtered = [...filtered].sort((a, b) => {
    const dateA = a.startDate || '';
    const dateB = b.startDate || '';
    return sort === 'desc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
  });

  const toastsNode = toasts.length > 0 && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
        <div className="nd-toasts">
          {toasts.map(t => (
            <div key={t.id} className={`nd-toast${t.tone ? ` ${t.tone}` : ''}`}>{t.text}</div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`nd-lvWrap${isMobile ? ' nd-mobile' : ''}`}>
      <div className="nd-lvSheet">
        <div className="nd-sheetHead">
          <div className="nd-sheetIco">🌴</div>
          <h2>
            Заявка на отгулы
            <span>отгулы · отпуска · больничные — заявки и одобрение</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={onClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-lvFlt">
          <span className="nd-fIco"><FiFilter /></span>
          <select value={filter} onChange={e => setFilter(e.target.value)} title="Фильтр заявок">
            <option value="all">Все</option>
            <optgroup label="Тип">
              <option value="type:leave">Отгул</option>
              <option value="type:vacation">Отпуск</option>
              <option value="type:sick">Больничный</option>
              <option value="type:remote">На удаленке</option>
            </optgroup>
            <optgroup label="Период">
              <option value="future">Будущие</option>
              <option value="past">Прошедшие</option>
            </optgroup>
          </select>
          <span className="nd-fIco"><TbArrowsSort /></span>
          <select value={sort} onChange={e => setSort(e.target.value)} title="Сортировка">
            <option value="desc">Сначала новые</option>
            <option value="asc">Сначала старые</option>
          </select>
          <span className="nd-lvTotal">Всего: <b>{filtered.length}</b></span>
        </div>

        <form className="nd-lvForm nd-glass" onSubmit={handleSubmit}>
          <div className="nd-lvGrid">
            <div className="nd-lvFld">
              <label htmlFor="lv-start">С даты</label>
              <input id="lv-start" name="startDate" type="date" value={form.startDate} onChange={handleChange} required />
            </div>
            <div className="nd-lvFld">
              <label htmlFor="lv-end">По дату</label>
              <input id="lv-end" name="endDate" type="date" value={form.endDate} onChange={handleChange} required />
            </div>
            <div className="nd-lvFld nd-full">
              <label htmlFor="lv-reason">Причина</label>
              <input
                id="lv-reason"
                name="reason"
                placeholder="Например: личные дела / к врачу"
                value={form.reason}
                onChange={handleChange}
                required
              />
            </div>
            <div className="nd-lvFld">
              <label htmlFor="lv-type">Тип</label>
              <select id="lv-type" name="type" value={form.type} onChange={handleChange}>
                <option value="leave">Отгул</option>
                <option value="vacation">Отпуск</option>
                <option value="sick">Больничный</option>
                <option value="remote">На удаленке</option>
              </select>
            </div>
            {form.type === 'leave' && (
              <div className="nd-lvFld">
                <label>Отгул на</label>
                <div className="nd-durRow">
                  <select name="hours" value={form.hours} onChange={handleChange} title="Часы отгула">
                    {[...Array(9).keys()].map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span>ч</span>
                  <select name="minutesOnly" value={form.minutesOnly} onChange={handleChange} title="Минуты отгула">
                    {[0, 5, 10, 15, 20, 30, 45].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span>мин</span>
                </div>
                <div className="nd-hint">Оставьте 0 ч 0 мин для полного дня</div>
              </div>
            )}
            <button type="submit" className="nd-addBtn" disabled={saving}>
              ＋ {saving ? 'Сохраняем…' : 'Добавить'}
            </button>
          </div>
        </form>

        <div className="nd-lvList">
          {filtered.length === 0 ? (
            <div className="nd-lvEmpty">🍃 Заявок нет — добавьте первую выше</div>
          ) : (
            filtered.map((l, i) => {
              const type = LEAVE_TYPES[l.type] || LEAVE_TYPES.leave;
              const status = l.status || 'pending';
              const statusMeta = status === 'approved'
                ? { cls: 'nd-ok', text: '✔ Одобрено' }
                : status === 'declined'
                  ? { cls: 'nd-no', text: '✕ Отклонено' }
                  : status === 'completed'
                    ? { cls: 'nd-done', text: '🎯 Отработано' }
                    : { cls: 'nd-pend', text: '⏳ На одобрении' };

              return (
                <div
                  key={l.id}
                  className={`nd-lvCard nd-glass${removingIds.includes(l.id) ? ' nd-out' : ''}`}
                  style={{ '--sc': type.color, animationDelay: `${Math.min(i * 60, 400)}ms` }}
                >
                  <div className="nd-rAv">
                    {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(userName)}
                  </div>

                  <div className="nd-rMain">
                    <span className="nd-rDates">{formatDate(l.startDate)} — {formatDate(l.endDate)}</span>
                    <span className="nd-rReason">{l.reason || 'Без причины'}</span>
                    <div className="nd-rChips">
                      <span className={`nd-tyC ${type.cls}`}>{type.label}</span>
                      <span className="nd-durC">{formatDuration(l)}</span>
                      <span className="nd-whoC">👤 {userName}</span>
                    </div>
                  </div>

                  <div className="nd-rRight">
                    <span className={`nd-stB ${statusMeta.cls}`}>{statusMeta.text}</span>
                    <div className="nd-rActs">
                      {status === 'pending' && canApprove && (
                        <>
                          <button type="button" className="nd-aBtn nd-okB" onClick={() => handleStatus(l.id, 'approved')}>
                            ✔ Одобрить
                          </button>
                          <button type="button" className="nd-aBtn nd-noB" onClick={() => handleStatus(l.id, 'declined')}>
                            ✕ Отклонить
                          </button>
                        </>
                      )}
                      <button type="button" className="nd-aBtn nd-delB" onClick={() => handleDelete(l.id)} title="Удалить заявку">
                        🗑 Удалить
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {toastsNode}
    </div>
  );
}
